// S8 — multi-camera synchronized playback panel.
//
// Owns its own session state: picks cameras + a start timestamp, opens
// a playback session, then renders an N-tile grid driven by a single
// shared timeline. Seeking the scrubber posts /seek for the session
// and refreshes every tile's stream URL atomically.
//
// Speed control uses ``videoElement.playbackRate`` rather than hls.js
// because hls.js doesn't natively support speed changes; setting
// playbackRate works across every modern browser for HLS playback.
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import {
  ChevronsLeftIcon,
  ChevronsRightIcon,
  PauseIcon,
  PlayIcon,
  StepBackIcon,
  StepForwardIcon,
} from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  type PlaybackSession,
  type PlaybackStream,
  seekPlaybackSession,
  useCreatePlaybackSession,
} from "@/api/playback";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";


// Tailwind class names by camera count → grid layout. Picked to keep
// each tile roughly 16:9 in the available width.
function gridClassForCount(n: number): string {
  if (n <= 1) return "grid-cols-1";
  if (n <= 2) return "grid-cols-2";
  if (n <= 4) return "grid-cols-2";
  if (n <= 6) return "grid-cols-3";
  if (n <= 9) return "grid-cols-3";
  if (n <= 12) return "grid-cols-4";
  return "grid-cols-4";
}


const SPEED_OPTIONS = [0.5, 1, 2, 4, 8] as const;
type Speed = typeof SPEED_OPTIONS[number];


function ms2iso(d: Date): string {
  return d.toISOString();
}


// ---------------------------------------------------------------------------
// Tile — owns the hls.js lifecycle for one camera in the session.
// ---------------------------------------------------------------------------
const PlaybackTile = ({
  stream,
  speed,
  paused,
}: {
  stream: PlaybackStream;
  speed: Speed;
  paused: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream.presigned_url) return;
    // Reset previous hls.js instance before swapping URLs.
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const canNative = v.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (canNative) {
      v.src = stream.presigned_url;
    } else if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(stream.presigned_url);
      hls.attachMedia(v);
      hlsRef.current = hls;
    }
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [stream.presigned_url]);

  // Speed + pause are shared across every tile so the user sees a
  // synchronized session.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else void v.play().catch(() => { /* gesture may be required */ });
  }, [paused]);

  return (
    <div className="relative aspect-video overflow-hidden rounded-md border border-[#2d3f4f] bg-primary">
      <div className="absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/70 to-transparent px-2 py-1 text-[11px] font-medium text-white/90">
        {stream.camera_name}
        {speed !== 1 && (
          <span className="ml-2 rounded bg-[#ff6d00]/70 px-1 text-[10px]">
            {speed}×
          </span>
        )}
      </div>
      {stream.has_recording && stream.presigned_url ? (
        <video
          ref={videoRef}
          controls={false}
          playsInline
          muted
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#21313c] text-[11px] text-white/40">
          No recording at this timestamp
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Setup form — pick cameras + start timestamp.
// ---------------------------------------------------------------------------
function SessionSetup({
  onOpenSession,
}: {
  onOpenSession: (session: PlaybackSession) => void;
}) {
  const cameras = useCameras({ limit: 200 });
  const create = useCreatePlaybackSession();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [startTs, setStartTs] = useState<string>(() =>
    new Date(Date.now() - 60 * 60 * 1000).toISOString().slice(0, 16),
  );
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 16) next.add(id);
      return next;
    });
  }

  function quickRelative(hoursBack: number) {
    setStartTs(
      new Date(Date.now() - hoursBack * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16),
    );
  }

  async function open() {
    setError(null);
    if (selected.size === 0) {
      setError("Select at least one camera");
      return;
    }
    try {
      const session = await create.mutateAsync({
        camera_ids: Array.from(selected),
        start_ts: new Date(`${startTs}:00Z`).toISOString(),
      });
      onOpenSession(session);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to open session");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">New playback session</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="mb-1 block text-xs">
            Cameras ({selected.size} selected · max 16)
          </Label>
          <div className="max-h-48 overflow-y-auto rounded border border-border p-2">
            {(cameras.data?.items ?? []).map((c) => (
              <label
                key={c.id}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted",
                  selected.has(c.id) && "bg-[#ff6d00]/10",
                )}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  disabled={!selected.has(c.id) && selected.size >= 16}
                />
                <span className="flex-1 truncate">{c.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-1 block text-xs">Start timestamp (UTC)</Label>
          <div className="flex gap-2">
            <Input
              type="datetime-local"
              value={startTs}
              onChange={(e) => setStartTs(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => quickRelative(1)}
            >
              − 1h
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => quickRelative(24)}
            >
              − 24h
            </Button>
          </div>
        </div>
        {error && (
          <div className="rounded bg-red-50 px-2 py-1 text-xs text-red-700">
            {error}
          </div>
        )}
        <Button onClick={() => void open()} disabled={create.isPending}>
          {create.isPending ? "Opening…" : "Open session"}
        </Button>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Active session — N-tile grid + shared timeline + speed controls.
// ---------------------------------------------------------------------------
function ActiveSession({
  session,
  onClose,
}: {
  session: PlaybackSession;
  onClose: () => void;
}) {
  const [streams, setStreams] = useState<PlaybackStream[]>(session.streams);
  const [currentTs, setCurrentTs] = useState<string>(session.playback_start_ts);
  const [speed, setSpeed] = useState<Speed>(1);
  const [paused, setPaused] = useState(false);
  const [seeking, setSeeking] = useState(false);

  async function seek(toIso: string) {
    setSeeking(true);
    try {
      const updated = await seekPlaybackSession(session.session_id, toIso);
      setStreams(updated.streams);
      setCurrentTs(updated.playback_start_ts);
    } finally {
      setSeeking(false);
    }
  }

  function jump(seconds: number) {
    const next = new Date(new Date(currentTs).getTime() + seconds * 1000);
    void seek(ms2iso(next));
  }

  function stepFrame(direction: 1 | -1) {
    // Frame-step assumption: 25 fps → 40 ms per frame.
    // Without a paused video we'd just be issuing seeks, so guard.
    if (!paused) return;
    jump(direction * 0.04);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          ← Close session
        </Button>
        <span className="text-xs text-muted-foreground">
          {streams.length} cameras · {new Date(currentTs).toLocaleString()}
        </span>
        {seeking && (
          <span className="text-xs text-[#ff6d00]">Seeking…</span>
        )}
      </div>

      <div className={cn("grid gap-2", gridClassForCount(streams.length))}>
        {streams.map((s) => (
          <PlaybackTile
            key={s.camera_id}
            stream={s}
            speed={speed}
            paused={paused}
          />
        ))}
      </div>

      {/* Shared transport controls */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => jump(-30)}
              title="Jump back 30s"
            >
              <ChevronsLeftIcon className="mr-1 h-3.5 w-3.5" /> 30s
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => stepFrame(-1)}
              disabled={!paused}
              title="Step backward (paused only)"
            >
              <StepBackIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaused((p) => !p)}
            >
              {paused ? (
                <>
                  <PlayIcon className="mr-1 h-3.5 w-3.5" /> Play
                </>
              ) : (
                <>
                  <PauseIcon className="mr-1 h-3.5 w-3.5" /> Pause
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => stepFrame(1)}
              disabled={!paused}
              title="Step forward (paused only)"
            >
              <StepForwardIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => jump(30)}
              title="Jump forward 30s"
            >
              30s <ChevronsRightIcon className="ml-1 h-3.5 w-3.5" />
            </Button>
            <div className="ml-2 inline-flex items-center gap-1 rounded border border-border p-0.5">
              {SPEED_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSpeed(opt)}
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] transition",
                    speed === opt
                      ? "border-[#ff6d00] bg-[#ff6d00]/15 text-[#ff6d00] border"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {opt}×
                </button>
              ))}
            </div>
            <div className="ml-auto inline-flex items-center gap-2">
              <Label className="text-[11px]">Seek to:</Label>
              <Input
                type="datetime-local"
                value={currentTs.slice(0, 16)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  void seek(new Date(`${v}:00Z`).toISOString());
                }}
                className="h-7 w-52 text-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Top-level wrapper rendered as a tab on the RecordingsPage.
// ---------------------------------------------------------------------------
export function MultiCameraPlayback() {
  const [session, setSession] = useState<PlaybackSession | null>(null);
  return session ? (
    <ActiveSession session={session} onClose={() => setSession(null)} />
  ) : (
    <SessionSetup onOpenSession={setSession} />
  );
}
